import { ConfirmDialog } from "@/components/ConfirmDialog";
import { ROLE_LABEL } from "@/components/boo/access-copy";
import { BooMark } from "@/components/boo/BooMark";
import { DataTable } from "@/components/DataTable";
import { EditSheet } from "@/components/EditSheet";
import { ErrorComponent, InlineError, LoadingIndicator } from "@/components/QueryState";
import { StatusBadge } from "@/components/StatusBadge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, type AdminContainerRow, type AppRow, type BooAccessGrant, type BooRole, type InvitationRow, type OrgMember, type OrgRow, type OrgStatus } from "@/lib/api";
import { formatDate, relativeIso } from "@/lib/format";
import { cn } from "@/lib/utils";
import { OrgSignInPanel } from "./OrgSignInPanel";
import { OrgModelsPanel } from "./OrgModelsPanel";
import { CreateSelfHostedBooDialog } from "./SelfHostedBoo";
import { SELF_HOSTED } from "@/lib/self-hosted";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { type ColumnDef } from "@tanstack/react-table";
import { ArrowLeft, ChevronDown, ExternalLink, Server, UserPlus } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { OrgBillingPanel } from "./OrgBillingPanel";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

const ORG_STATUS_HELP: Record<OrgStatus, string> = {
  active: "Live customer — billing alerts on",
  test: "Internal workspace — no billing alerts",
  dormant: "Quiet customer — no billing alerts",
};

// ── Column definitions ───────────────────────────────────────────

const MEMBER_ROLES = ["owner", "admin", "billing_admin", "member"] as const;
const MEMBER_ROLE_LABEL: Record<string, string> = {
  owner: "Owner",
  admin: "Admin",
  billing_admin: "Billing admin",
  member: "Member",
};

// Built as a factory so the role cell can drive the page's role-change mutation.
// A site admin can set any role here (the last-owner demotion is refused by the
// backend). Ownership included deliberately — this is the super-admin console.
function makeMemberColumns(
  onChangeRole: (userId: string, role: string) => void,
  changingUserId: string | null,
): ColumnDef<OrgMember>[] {
  return [
    { accessorKey: "userName", header: "Name" },
    { accessorKey: "userEmail", header: "Email" },
    {
      accessorKey: "role",
      header: "Role",
      cell: ({ row }) => {
        const m = row.original;
        const current = String(m.role);
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-7 gap-1 font-normal"
                loading={changingUserId === m.userId}
              >
                {MEMBER_ROLE_LABEL[current] ?? current}
                <ChevronDown className="size-3.5 opacity-50" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {MEMBER_ROLES.map((r) => (
                <DropdownMenuItem
                  key={r}
                  disabled={r === current}
                  onClick={() => onChangeRole(m.userId, r)}
                >
                  {MEMBER_ROLE_LABEL[r]}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
    {
      accessorKey: "createdAt",
      header: "Joined",
      cell: ({ getValue }) => formatDate(getValue() as string),
    },
  ];
}

const containerColumns: ColumnDef<AdminContainerRow>[] = [
  {
    accessorKey: "name",
    header: "Name",
    cell: ({ row }) => (
      <span className="inline-flex items-center gap-2">
        {row.original.name}
        {row.original.hostingTarget === SELF_HOSTED && <Badge variant="outline">Self-hosted</Badge>}
      </span>
    ),
  },
  {
    accessorKey: "deploymentStatus",
    header: "Status",
    cell: ({ row }) => (
      <StatusBadge status={row.original.deploymentStatus} errorMessage={row.original.errorMessage} />
    ),
  },
  {
    accessorKey: "lastActivityAt",
    header: "Last Activity",
    cell: ({ getValue }) => (
      <span className="text-muted-foreground">{relativeIso(getValue() as string | null)}</span>
    ),
  },
  {
    accessorKey: "taskCount",
    header: "Tasks",
    cell: ({ getValue }) => <span className="tabular-nums">{getValue() as number}</span>,
  },
];

// Built as a factory so the Resend action can close over the page's resend
// handler. Resending a pending invite resets its 48h clock and re-emails the
// link — so a lost invitation isn't a lockout until it expires.
function makeInvitationColumns(
  onResend: (inv: InvitationRow) => void,
  resendingId: string | null,
): ColumnDef<InvitationRow>[] {
  return [
    { accessorKey: "email", header: "Email" },
    {
      accessorKey: "role",
      header: "Role",
      cell: ({ getValue }) => (
        <Badge variant="secondary">{String(getValue() ?? "member")}</Badge>
      ),
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ getValue }) => {
        const status = getValue() as string;
        return (
          <Badge variant={status === "pending" ? "outline" : "secondary"}>
            {status}
          </Badge>
        );
      },
    },
    {
      accessorKey: "expiresAt",
      header: "Expires",
      cell: ({ getValue }) => formatDate(getValue() as string),
    },
    {
      accessorKey: "emailError",
      header: "Email Error",
      cell: ({ getValue }) => {
        const err = getValue() as string | null;
        return err ? (
          <span className="text-destructive text-xs">{err}</span>
        ) : (
          <span className="text-muted-foreground">{"\u2014"}</span>
        );
      },
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => {
        const inv = row.original;
        if (inv.status !== "pending") return null;
        return (
          <Button
            variant="outline"
            size="sm"
            loading={resendingId === inv.id}
            onClick={() => onResend(inv)}
          >
            Resend
          </Button>
        );
      },
    },
  ];
}

const appColumns: ColumnDef<AppRow>[] = [
  { accessorKey: "name", header: "Name" },
  {
    accessorKey: "description",
    header: "Description",
    cell: ({ getValue }) => (
      <span className="text-muted-foreground">{(getValue() as string) ?? "\u2014"}</span>
    ),
  },
  {
    accessorKey: "createdAt",
    header: "Created",
    cell: ({ getValue }) => formatDate(getValue() as string),
  },
];

// ── Field definitions for edit sheets ────────────────────────────

const ORG_FIELDS = [
  { key: "id", label: "ID", readOnly: true },
  { key: "name", label: "Name" },
  { key: "slug", label: "Slug" },
];

const APP_FIELDS = [
  { key: "id", label: "ID", readOnly: true },
  { key: "name", label: "Name" },
  { key: "description", label: "Description" },
];

// ── Read-only detail row ─────────────────────────────────────────

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-muted-foreground text-xs">{label}</Label>
      <div className="text-sm">{value || <span className="text-muted-foreground">{"\u2014"}</span>}</div>
    </div>
  );
}

// ── Invite dialog ────────────────────────────────────────────────
//
// Admin-console counterpart to the org-context invite on MembersPage. Because
// the whole admin router is super-admin gated, a site admin can invite into
// any org from here without joining it. Org roles are admin/member only —
// org ownership is never granted by invite.
//
// An org role alone opens no Boo: since per-Boo roles, someone needs a role
// in a Boo to see more than its spend. The invite carries that Boo access
// (applied when they accept), so pick it here or they join to a dashboard
// with nothing to open.

const INVITE_ROLES = ["admin", "billing_admin", "member"] as const;
type InviteRole = (typeof INVITE_ROLES)[number];
const INVITE_ROLE_LABEL: Record<InviteRole, string> = {
  admin: "Admin",
  billing_admin: "Billing admin",
  member: "Member",
};

type BooChoice = "none" | BooRole;
const CHOICE_LABEL: Record<BooChoice, string> = { none: "No access", ...ROLE_LABEL };
const BOO_CHOICES: BooChoice[] = ["none", "member", "admin", "owner"];

function InviteMemberDialog({
  open,
  onClose,
  orgId,
  orgName,
  existingEmails,
  pendingEmails,
}: {
  open: boolean;
  onClose: () => void;
  orgId: string;
  orgName: string;
  existingEmails: Set<string>;
  pendingEmails: Set<string>;
}) {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<InviteRole>("member");
  const [picked, setPicked] = useState<Record<string, BooChoice>>({});
  // Same key as the page's Boos tab, so the list is usually already cached.
  const boosQuery = useQuery({
    queryKey: ["admin-org-containers", orgId],
    queryFn: () => api.admin.orgContainers(orgId),
    enabled: open,
  });
  const boos = boosQuery.data ?? [];
  const choiceFor = (boo: AdminContainerRow): BooChoice => picked[boo.id] ?? "none";
  const grants: BooAccessGrant[] = boos.flatMap((b) => {
    const c = choiceFor(b);
    return c === "none" ? [] : [{ containerId: b.id, role: c }];
  });

  const normalizedEmail = email.trim().toLowerCase();
  // A live pending invite for this email isn't a hard block: sending again
  // resends it (resets the 48h clock + re-emails), so a lost link recovers.
  const isResend = pendingEmails.has(normalizedEmail);
  // Picks don't outlive the dialog: cancelling after choosing Owner on a Boo
  // shouldn't hand that out on the next open.
  const close = () => {
    setPicked({});
    onClose();
  };

  const invite = useMutation({
    mutationFn: () => {
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(normalizedEmail)) {
        throw new Error("Enter a valid email");
      }
      if (existingEmails.has(normalizedEmail)) {
        throw new Error("This person is already a member of this organization.");
      }
      // A resend with nothing picked keeps the Boo access the pending invite
      // already carries; anything picked replaces it.
      return api.admin.createOrgInvitation(orgId, {
        email: normalizedEmail,
        role,
        resend: isResend,
        ...(isResend && grants.length === 0 ? {} : { boos: grants }),
      });
    },
    onSuccess: (inv) => {
      queryClient.invalidateQueries({ queryKey: ["admin-org-invitations", orgId] });
      // The invite is created even when delivery fails (the row carries the
      // error and the link is copyable), so don't claim "sent" in that case —
      // warn, and leave the failure visible in the Email Error column.
      if (inv.emailError) {
        toast.warning(
          isResend
            ? "Invitation updated, but the email failed to send"
            : "Invitation created, but the email failed to send",
        );
      } else {
        toast.success(isResend ? "Invitation resent" : "Invitation sent");
      }
      setEmail("");
      setRole("member");
      close();
    },
  });

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Invite to {orgName}</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            invite.mutate();
          }}
          className="flex flex-col gap-4 pt-2"
        >
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="invite-email">Email</Label>
            <Input
              id="invite-email"
              type="email"
              placeholder="teammate@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
            />
            {isResend && (
              <p className="text-xs text-muted-foreground">
                A pending invite already exists for this email. Sending will
                resend it and reset the 48-hour expiry.
              </p>
            )}
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Role</Label>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="justify-between font-normal w-full">
                  {INVITE_ROLE_LABEL[role]}
                  <ChevronDown className="size-4 opacity-50" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                className="w-[--radix-dropdown-menu-trigger-width]"
              >
                {INVITE_ROLES.map((r) => (
                  <DropdownMenuItem key={r} onClick={() => setRole(r)}>
                    {INVITE_ROLE_LABEL[r]}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium">Boo access</span>
            {boosQuery.isPending ? (
              <div className="h-12 animate-pulse rounded-lg bg-muted" />
            ) : boosQuery.isError ? (
              // A failed load must not read as "no Boos": the invite would go
              // out with no access and the admin none the wiser.
              <div className="flex flex-col gap-2">
                <InlineError error={boosQuery.error} />
                <div>
                  <Button type="button" variant="outline" size="sm" onClick={() => boosQuery.refetch()}>
                    Try again
                  </Button>
                </div>
              </div>
            ) : boos.length === 0 ? (
              <p className="rounded-lg bg-muted/50 px-3 py-2.5 text-sm text-muted-foreground">
                This organization has no Boos yet, so they&apos;ll join without access to one.
              </p>
            ) : (
              <ul className="flex max-h-64 flex-col divide-y overflow-y-auto rounded-lg border">
                {boos.map((boo) => {
                  const choice = choiceFor(boo);
                  return (
                    <li key={boo.id} className="flex items-center gap-3 px-3 py-2">
                      <BooMark id={boo.id} className="size-5 shrink-0" muted={choice === "none"} />
                      <span className={cn("min-w-0 flex-1 truncate text-sm", choice === "none" && "text-muted-foreground")}>
                        {boo.name}
                      </span>
                      <Select value={choice} onValueChange={(v) => setPicked((p) => ({ ...p, [boo.id]: v as BooChoice }))}>
                        <SelectTrigger className="h-8 w-[8.75rem] shrink-0" aria-label={`Access to ${boo.name}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent align="end">
                          {BOO_CHOICES.map((c) => (
                            <SelectItem key={c} value={c}>
                              {CHOICE_LABEL[c]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </li>
                  );
                })}
              </ul>
            )}
            {(boos.length > 0 || boosQuery.isError) && grants.length === 0 && (
              <p className="text-xs text-muted-foreground">
                {isResend
                  ? "Nothing picked keeps the Boo access this invite already carries."
                  : "With no Boo picked they'll see only spend, until a Boo's owner adds them."}
              </p>
            )}
          </div>
          {invite.isError && <InlineError error={invite.error} />}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={close}>
              Cancel
            </Button>
            {/* Not until the Boo list has answered: sending blind would hand
                out no access without the admin knowing the list was missing. */}
            <Button type="submit" loading={invite.isPending} disabled={boosQuery.isPending}>
              {isResend ? "Resend invite" : "Send invite"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Page component ───────────────────────────────────────────────

export function AdminOrgDetailPage() {
  useDocumentTitle("Admin: Organization details");
  const { orgId } = useParams({ strict: false }) as { orgId: string };
  const { tab } = useSearch({ strict: false }) as { tab?: string };
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [editOrg, setEditOrg] = useState(false);
  const [selectedApp, setSelectedApp] = useState<AppRow | null>(null);
  const [selectedContainer, setSelectedContainer] = useState<AdminContainerRow | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [selfHostedOpen, setSelfHostedOpen] = useState(false);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [teardownOpen, setTeardownOpen] = useState(false);

  // Org data
  const { data: orgsResponse, isLoading: orgsLoading, isError: orgsError, error: orgsErr } = useQuery({
    queryKey: ["admin-organizations"],
    queryFn: api.admin.organizations,
  });
  const orgs = orgsResponse?.data;
  const orgsPermissions = orgsResponse?._permissions;
  const org = orgs?.find((o) => o.id === orgId) as OrgRow | undefined;

  // Members
  const { data: membersResponse, isLoading: membersLoading, isError: membersError, error: membersErr } = useQuery({
    queryKey: ["admin-org-members", orgId],
    queryFn: () => api.admin.orgMembers(orgId),
    enabled: !!orgId,
  });
  const members = membersResponse?.data;

  const updateMemberRole = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string }) =>
      api.admin.updateMemberRole(orgId, userId, role),
    onSuccess: () => {
      toast.success("Role updated");
      queryClient.invalidateQueries({ queryKey: ["admin-org-members", orgId] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Failed to update role"),
  });
  const memberColumns = makeMemberColumns(
    (userId, role) => updateMemberRole.mutate({ userId, role }),
    updateMemberRole.isPending ? (updateMemberRole.variables?.userId ?? null) : null,
  );

  // Containers
  const { data: containerRows, isLoading: containersLoading, isError: containersError, error: containersErr } = useQuery({
    queryKey: ["admin-org-containers", orgId],
    queryFn: () => api.admin.orgContainers(orgId),
    enabled: !!orgId,
  });

  // Invitations
  const { data: invitations, isLoading: invitationsLoading, isError: invitationsError, error: invitationsErr } = useQuery({
    queryKey: ["admin-org-invitations", orgId],
    queryFn: () => api.admin.orgInvitations(orgId),
    enabled: !!orgId,
  });

  // Apps
  const { data: apps, isLoading: appsLoading, isError: appsError, error: appsErr } = useQuery({
    queryKey: ["admin-org-apps", orgId],
    queryFn: () => api.admin.orgApps(orgId),
    enabled: !!orgId,
  });

  // Teardown precondition: is the org deletable, and if not, which Boos block it.
  const { data: teardown } = useQuery({
    queryKey: ["admin-org-teardown-preflight", orgId],
    queryFn: () => api.admin.orgTeardownPreflight(orgId),
    enabled: !!orgId,
  });
  // `=== true` (not `!== false`) so the button stays hidden until the preflight
  // confirms the viewer may delete — avoids a brief disabled-button flash while
  // the query is in flight.
  const canDeleteOrg = teardown?._permissions?.canDelete === true;
  const liveContainers = teardown?.liveContainers ?? [];
  const deleteBlocked = !teardown || liveContainers.length > 0;

  // Mutations
  const orgMutation = useMutation({
    mutationFn: (data: Record<string, string>) =>
      api.admin.updateOrganization(orgId, { name: data.name, slug: data.slug || undefined }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-organizations"] });
      // The orgs table reads the enriched list under a different key; invalidate
      // it too so a rename/slug edit shows without a manual page refresh.
      queryClient.invalidateQueries({ queryKey: ["admin-organizations-enriched"] });
      setEditOrg(false);
    },
  });

  const deleteOrg = useMutation({
    mutationFn: () => api.admin.deleteOrganization(orgId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-organizations"] });
      queryClient.invalidateQueries({ queryKey: ["admin-organizations-enriched"] });
      setTeardownOpen(false);
      toast.success(`Deleted ${org?.name ?? "organization"}`);
      navigate({ to: "/superadmin/organizations" });
    },
    // Fail loud — surface the backend's reason (e.g. the "tear down its Boos
    // first" 409 guard) instead of a generic message.
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Delete failed");
    },
  });

  const authMethodsMutation = useMutation({
    mutationFn: (ssoOnly: boolean) =>
      api.admin.updateOrganization(orgId, {
        allowedAuthMethods: ssoOnly ? ["google", "microsoft"] : null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-organizations"] });
    },
  });

  const statusMutation = useMutation({
    mutationFn: (status: OrgStatus) => api.admin.updateOrganization(orgId, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-organizations"] });
      queryClient.invalidateQueries({ queryKey: ["admin-organizations-enriched"] });
    },
  });

  const appMutation = useMutation({
    mutationFn: (data: Record<string, string>) =>
      api.apps.update(selectedApp!.id, { name: data.name, description: data.description }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-org-apps", orgId] });
      setSelectedApp(null);
    },
  });

  // Resend a pending invite: resets its 48h clock and re-emails the link.
  const resendInvitation = useMutation({
    mutationFn: (inv: InvitationRow) => {
      setResendingId(inv.id);
      return api.admin.createOrgInvitation(orgId, {
        email: inv.email,
        role: (inv.role === "admin" ? "admin" : "member"),
        resend: true,
      });
    },
    onSuccess: (inv) => {
      queryClient.invalidateQueries({ queryKey: ["admin-org-invitations", orgId] });
      // Same as the dialog: a delivery failure still returns a 201, so warn
      // rather than falsely reporting the resend succeeded.
      if (inv.emailError) {
        toast.warning("Invitation updated, but the email failed to send");
      } else {
        toast.success("Invitation resent");
      }
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Resend failed");
    },
    onSettled: () => setResendingId(null),
  });

  const invitationColumns = makeInvitationColumns(
    (inv) => resendInvitation.mutate(inv),
    resendingId,
  );

  if (orgsLoading) return <LoadingIndicator />;
  if (orgsError || !orgs) return <ErrorComponent error={orgsErr ?? "Failed to load organizations"} />;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="space-y-3">
        <Link
          to="/superadmin/organizations"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="size-4" />
          Organizations
        </Link>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              {org?.name ?? orgId}
            </h1>
            {org?.slug && (
              <p className="text-sm text-muted-foreground font-mono mt-0.5">
                {org.slug}
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {orgsPermissions?.canEditName !== false && (
              <Button variant="outline" size="sm" onClick={() => setEditOrg(true)}>
                Edit
              </Button>
            )}
            {canDeleteOrg && (
              <Button
                variant="destructive"
                size="sm"
                disabled={deleteBlocked}
                onClick={() => setTeardownOpen(true)}
                title={
                  deleteBlocked
                    ? `Tear down this organization's ${liveContainers.length} Boo${liveContainers.length === 1 ? "" : "s"} first`
                    : undefined
                }
              >
                Delete
              </Button>
            )}
          </div>
        </div>
        {canDeleteOrg && deleteBlocked && liveContainers.length > 0 && (
          <p className="text-xs text-muted-foreground">
            Can't delete yet — tear down its {liveContainers.length} live Boo
            {liveContainers.length === 1 ? "" : "s"} first.
          </p>
        )}
        {orgsPermissions?.canEditAuthMethods !== false && org && (
          <div className="flex items-center gap-2 pt-1">
            <Switch
              id="sso-only-signin"
              checked={org.allowedAuthMethods != null &&
                !org.allowedAuthMethods.includes("email_otp")}
              disabled={authMethodsMutation.isPending}
              onCheckedChange={(checked) => authMethodsMutation.mutate(checked)}
            />
            <Label htmlFor="sso-only-signin" className="text-sm font-normal">
              SSO-only sign-in
              <span className="text-muted-foreground">
                {" "}
                — members can't use email codes
              </span>
            </Label>
            {authMethodsMutation.isError && (
              <span className="text-sm text-destructive">Failed to update</span>
            )}
          </div>
        )}
        {orgsPermissions?.canEditStatus !== false && org && (
          <div className="flex items-center gap-2 pt-1">
            <Label htmlFor="org-status" className="text-sm font-normal">
              Status
            </Label>
            <Select
              value={org.status ?? "active"}
              disabled={statusMutation.isPending}
              onValueChange={(v) => statusMutation.mutate(v as OrgStatus)}
            >
              <SelectTrigger id="org-status" size="sm" className="w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="test">Test</SelectItem>
                <SelectItem value="dormant">Dormant</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-sm text-muted-foreground">
              {ORG_STATUS_HELP[org.status ?? "active"]}
            </span>
            {statusMutation.isError && (
              <span className="text-sm text-destructive">Failed to update</span>
            )}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={teardownOpen}
        onOpenChange={setTeardownOpen}
        title="Delete organization?"
        description={
          "Soft-deletes this organization — its record is kept for billing history, not dropped, and it's hidden from everyone. This can't be undone from the dashboard."
        }
        confirmLabel="Delete organization"
        confirmPhrase={org?.name ?? teardown?.org.name}
        loading={deleteOrg.isPending}
        onConfirm={() => deleteOrg.mutate()}
      />

      {/* Tabbed content */}
      <Tabs
        value={tab ?? "members"}
        onValueChange={(value) =>
          navigate({
            to: "/superadmin/organizations/$orgId",
            params: { orgId },
            search: value === "members" ? {} : { tab: value },
            replace: true,
          })
        }
      >
        <div className="-mx-1 overflow-x-auto px-1">
          <TabsList variant="line">
            <TabsTrigger value="members">
              Members{members ? ` (${members.length})` : ""}
            </TabsTrigger>
            <TabsTrigger value="containers">
              Containers{containerRows ? ` (${containerRows.length})` : ""}
            </TabsTrigger>
            <TabsTrigger value="invitations">
              Invitations{invitations ? ` (${invitations.length})` : ""}
            </TabsTrigger>
            <TabsTrigger value="apps">
              Apps{apps ? ` (${apps.length})` : ""}
            </TabsTrigger>
            <TabsTrigger value="billing">Billing</TabsTrigger>
            <TabsTrigger value="sign-in">Sign-in</TabsTrigger>
            <TabsTrigger value="models">Models</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="members" className="mt-4">
          {membersError ? (
            <InlineError error={membersErr ?? "Failed to load members"} />
          ) : (
            <DataTable columns={memberColumns} data={members ?? []} isLoading={membersLoading} />
          )}
        </TabsContent>

        <TabsContent value="containers" className="mt-4 space-y-3">
          <div className="flex justify-end">
            <Button size="sm" variant="outline" onClick={() => setSelfHostedOpen(true)}>
              <Server className="size-4" />
              New self-hosted Boo
            </Button>
          </div>
          {containersError ? (
            <InlineError error={containersErr ?? "Failed to load containers"} />
          ) : (
            <DataTable
              columns={containerColumns}
              data={containerRows ?? []}
              isLoading={containersLoading}
              onRowClick={setSelectedContainer}
            />
          )}
        </TabsContent>

        <TabsContent value="invitations" className="mt-4 space-y-3">
          <div className="flex justify-end">
            <Button size="sm" onClick={() => setInviteOpen(true)}>
              <UserPlus className="size-4" />
              Invite member
            </Button>
          </div>
          {invitationsError ? (
            <InlineError error={invitationsErr ?? "Failed to load invitations"} />
          ) : (
            <DataTable columns={invitationColumns} data={invitations ?? []} isLoading={invitationsLoading} />
          )}
        </TabsContent>

        <TabsContent value="apps" className="mt-4">
          {appsError ? (
            <InlineError error={appsErr ?? "Failed to load apps"} />
          ) : (
            <DataTable
              columns={appColumns}
              data={apps ?? []}
              isLoading={appsLoading}
              onRowClick={orgsPermissions?.canEditName !== false ? setSelectedApp : undefined}
            />
          )}
        </TabsContent>

        <TabsContent value="billing" className="mt-4">
          <OrgBillingPanel orgId={orgId} />
        </TabsContent>

        <TabsContent value="sign-in" className="mt-4">
          <OrgSignInPanel orgId={orgId} />
        </TabsContent>

        <TabsContent value="models" className="mt-4">
          <OrgModelsPanel orgId={orgId} />
        </TabsContent>
      </Tabs>

      {/* Invite member dialog */}
      <CreateSelfHostedBooDialog
        open={selfHostedOpen}
        onClose={() => setSelfHostedOpen(false)}
        orgId={orgId}
        members={members ?? []}
      />
      <InviteMemberDialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        orgId={orgId}
        orgName={org?.name ?? orgId}
        existingEmails={new Set((members ?? []).map((m) => m.userEmail.toLowerCase()))}
        pendingEmails={
          new Set(
            (invitations ?? [])
              .filter((i) => i.status === "pending")
              .map((i) => i.email.toLowerCase()),
          )
        }
      />

      {/* Org edit sheet */}
      <EditSheet
        open={editOrg}
        onClose={() => setEditOrg(false)}
        title="Edit Organization"
        item={(org as unknown as Record<string, unknown>) ?? null}
        fields={ORG_FIELDS}
        onSave={orgMutation.mutateAsync}
        isSaving={orgMutation.isPending}
      />

      {/* App edit sheet */}
      <EditSheet
        open={!!selectedApp}
        onClose={() => setSelectedApp(null)}
        title="Edit App"
        item={selectedApp as Record<string, unknown> | null}
        fields={APP_FIELDS}
        onSave={appMutation.mutateAsync}
        isSaving={appMutation.isPending}
      />

      {/* Container detail sheet (read-only) */}
      <Sheet open={!!selectedContainer} onOpenChange={(o) => !o && setSelectedContainer(null)}>
        <SheetContent className="sm:max-w-md p-6">
          <SheetHeader>
            <SheetTitle>{selectedContainer?.name ?? "Container"}</SheetTitle>
          </SheetHeader>
          {selectedContainer && (
            <div className="mt-6 flex flex-col gap-4">
              <DetailRow label="ID" value={<span className="font-mono text-xs">{selectedContainer.id}</span>} />
              <DetailRow label="Status" value={<StatusBadge status={selectedContainer.deploymentStatus} errorMessage={selectedContainer.errorMessage} />} />
              {selectedContainer.errorMessage && (
                <DetailRow label="Error" value={<span className="text-destructive text-xs">{selectedContainer.errorMessage}</span>} />
              )}
              <DetailRow label="Tasks" value={selectedContainer.taskCount} />
              <DetailRow label="Last Activity" value={relativeIso(selectedContainer.lastActivityAt)} />
              <DetailRow label="Created" value={formatDate(selectedContainer.createdAt)} />
              {/* orgRoute.beforeLoad auto-runs setActive on click, so an admin
                  viewing another org bounces into that org's dashboard. */}
              <Button asChild variant="outline" size="sm" className="self-start">
                <Link
                  to="/orgs/$orgId/containers/$containerId/chat"
                  params={{ orgId, containerId: selectedContainer.id }}
                >
                  <ExternalLink className="size-3.5" />
                  Open in dashboard
                </Link>
              </Button>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
